import { CalendarViewMode, CalendarObjectType } from "../enumerations";
import { LABEL_COLORS } from "../utils/constants";
import * as modelsGet from "../models/get";
import * as modelsCalendar from "../models/calendar";
import * as modelsChangeset from "../models/changeset";
import * as modelsStorage from "../models/storage";
import { getCalendar } from "../api/calendars";
import { getCalendarGroup } from "../api/calendarGroups";
import { getEventLinks } from "../api/events";
import { getLastChangeset } from "../api/changesets";
import { getUser } from "../api/users";
import * as moment from "moment";
import { STORAGE_KEYS } from "../utils/constants";
import { getPixelOutOfMinutes } from "../utils/utils";
import * as localStorage from "../utils/localstorage";
import { Time } from "../types/time";

/**
 * Ist dazu da um ein @see ISelectedObjectId Objekt zu parsen. Wenn erfolgreich wird entweder
`* ein @see ICalendar oder ein @see ICalendarGroup Objekt zurückgegeben.
 * @param obj Das Objekt welches geparsed werden soll.
 */
export function getSelectedObject(obj: modelsCalendar.ISelectedObjectId, calendarGroups: modelsGet.ICalendarGroup[], calendars: modelsGet.ICalendar[]): modelsCalendar.ISelectedObjectReturn
{
    if (obj == null)
    {
        const errorMsg = "obj cannot be null!";
        console.error(errorMsg);
        return {
            error: (callback: (message: string) => void) => callback && callback(errorMsg)
        };
    }

    var data: modelsGet.ICalendar | modelsGet.ICalendarGroup;
    const { type, id } = obj;

    if (type === CalendarObjectType.all)
        data = calendars.first();
    else if (type === CalendarObjectType.calendar)
        data = calendars.where(c => c.id === id).first();
    else
        data = calendarGroups.where(c => c.id === id).first();

    return {
        all: (callback: (calendar: modelsGet.ICalendar) => void) => callback && type === CalendarObjectType.all && callback(data as modelsGet.ICalendar),
        calendar: (callback: (calendar: modelsGet.ICalendar) => void) => callback && type === CalendarObjectType.calendar && callback(data as modelsGet.ICalendar),
        calendarGroup: (callback: (group: modelsGet.ICalendarGroup) => void) => callback && type === CalendarObjectType.calendarGroup && callback(data as modelsGet.ICalendarGroup),
    };
}

/**
 * Ist dazu da um ein @see ISelectedObjectId Objekt zu parsen. Wenn erfolgreich wird entweder
`* ein @see ICalendar oder ein @see ICalendarGroup Objekt zurückgegeben.
 * @param obj Das Objekt welches geparsed werden soll.
 */
export async function getSelectedObjectAsync(obj: modelsCalendar.ISelectedObjectId): Promise<modelsCalendar.ISelectedObjectReturn>
{
    if (obj == null)
    {
        const errorMsg = "obj cannot be null!";
        console.error(errorMsg);
        return {
            error: (callback: (message: string) => void) => callback && callback(errorMsg)
        };
    }

    var data: modelsGet.ICalendar | modelsGet.ICalendarGroup;
    const { type, id } = obj;

    if (type === CalendarObjectType.calendar)
        data = await getCalendar(id);
    else
        data = await getCalendarGroup(id);

    return {
        calendar: (callback: (calendar: modelsGet.ICalendar) => void) => callback && type === CalendarObjectType.calendar && callback(data as modelsGet.ICalendar),
        calendarGroup: (callback: (group: modelsGet.ICalendarGroup) => void) => callback && type === CalendarObjectType.calendarGroup && callback(data as modelsGet.ICalendarGroup),
    };
}

///**
// * Prüft ob der übergebene Benutzer berechtigt ist den übergebenen Termin einzusehen.
// * @param user Der Benutzer der gerpüft werden soll.
// * @param event Der Termin der geprüft werden soll
// */
//export function isUserAuthorizedToSeeEvent(user: IUser, evt: IEvent): boolean
//{
//    if (user.claims.can_see_all_events)
//        return true;

//    if (user.id === evt.user_id)
//    {
//        return true;
//    }
//    else
//    {
//        var mortician: IPerson;
//        if (evt.persons != null)
//        {
//            mortician = evt.persons.where(p => p.is_mortician).first();
//            if (mortician.id === user.mortician_id)
//                return true;
//        }
//    }

//    return false;
//}

/**
 * Prüft ob der Termin zum gerade selektierten Kalender gehört.
 * @param evt Der Termin der geprüft werden soll.
 * @param selectedCalendarId Der momentan selektierte Kalender.
 */
export function isEventFullyVisible(appointment: modelsGet.IAppointment | modelsGet.ITimeLock, selectedCalendarId: number): boolean
{
    if (appointment != null)
        return (appointment.calendar_id === selectedCalendarId)
    else
        return false;
};



/**
 * Ermittelt die richtige Termin Farbe.
 * @param user Der momentan angemeldete Benutzer.
 * @param evt Der Termin für den die Farbe ermittelt werden soll.
 */
export function getEventColor(appointment: modelsGet.IAppointment): string
{
    if (appointment == null)
        return "#9E9E9E";

    if (appointment.appointment_type === "not_authorized")
        return "#df4019";

    var color: string = "";
    if (appointment.status_id > 0)
    {
        color = appointment.status_color;
    }
    else
    {
        color = appointment.calendar_color;
    }

    if (!color.startsWith("#"))
    {
        color = LABEL_COLORS.where(c => c.name === color).first().color;
    }

    return color;
}

/**
 * Ermittelt die Anzahl der Verlinkungen in einem Termin.
 * @param eventId Die ID des Termins dessen Anzahl zurückgegeben werden soll.
 * @param parentEventId Der Ersttermin des übergebenen Termins.
 */
export async function getEventLinksCountAsync(eventId: number, parentEventId: number): Promise<number>
{
    var links = await getEventLinks(eventId, parentEventId);
    if (links != null)
        return links.length;
    else
        return 0;
}

/**
 * Gibt die verstorbene Person eines Termins zurück. Wenn eine zugeordnet ist.
 * @param evt Der Termin dessen Person zurückgegeben werden soll
 */
export function getDeceasedPerson(appointment: modelsGet.IAppointment): modelsGet.IPerson
{
    if (appointment != null && appointment.persons != null && appointment.persons.length > 0)
    {
        return appointment.persons.where(p => p.is_deceased).first();
    }

    return null;
}

/**
 * Gibt den Bestatter eines Termins zurück. Wenn eine zugeordnet ist.
 * @param evt Der Termin dessen Person zurückgegeben werden soll
 */
export function getMortician(appointment: modelsGet.IAppointment): modelsGet.IPerson
{
    if (appointment != null && appointment.persons != null && appointment.persons.length > 0)
    {
        return appointment.persons.where(p => p.is_mortician).first();
    }

    return null;
}

/**
 * Gibt einen Text zurück der die Komplette Zeitspanne (samt Rüstzeit) eines Termins wiederspiegelt.
 * @param evt Der Termin des Zeit zurückgegeben werden soll.
 */
export function getTimeWithSetuptimeText(appointment: modelsGet.IAppointment): string 
{
    var starttime: moment.Moment = moment(appointment.starttime);
    var endtime: moment.Moment = moment(appointment.endtime);
    var setuptimeBefore: number = appointment.setuptime_before;
    var setuptimeAfter: number = appointment.setuptime_after;

    if (setuptimeBefore > 0 && setuptimeAfter > 0)
        return starttime.subtract(setuptimeBefore, "minutes").format("HH:mm") + ' - ' + endtime.add(setuptimeAfter, "minutes").format("HH:mm");
    else if (setuptimeBefore > 0)
        return starttime.subtract(setuptimeBefore, "minutes").format("HH:mm") + ' - ' + endtime.format("HH:mm");
    else if (setuptimeAfter > 0)
        return starttime.format("HH:mm") + ' - ' + endtime.add(setuptimeAfter, "minutes").format("HH:mm");
}

/**
 * Gibt alle anzuzeigenden Labels zurück.
 * @param evt Der Termin dessen Labels zurückgegeben werden sollen.
 */
export function getLabels(appointment: modelsGet.IAppointment): modelsGet.ILabel[]
{
    if (appointment.labels != null && appointment.labels.length > 0)
    {
        var sortedLabels: modelsGet.ILabel[] = [];
        for (var i = appointment.labels.length - 1; i >= 0; i--)
        {
            if (appointment.labels[i].label_color !== "null")
                sortedLabels.push(appointment.labels[i]);
        }

        return sortedLabels;
    }
}

/**
 * Ermittelt den Benutzer der den Termin als letztes bearbeitet hat.
 * @param evt Der Termin dessen letzte Änderung ermittelt werden soll.
 */
export async function getLastTimeOfChangeAsync(appointment: modelsGet.IAppointment): Promise<modelsChangeset.ILastChange>
{
    var last = await getLastChangeset(appointment.id);
    if (last != null)
    {
        return {
            user: await getUser(last.user_id),
            change_date: moment(last.change_date)
        }
    }
    else
    {
        return {
            user: await getUser(appointment.user_id),
            change_date: moment(appointment.create_date)
        }
    }
}

/**
 * Generiert einen lesbaren formatierten String aus dem übergebenen Datum.
 * @param date Das Datum welches formatiert werden soll.
 * @param viewMode Kalenderansicht.
 * @param hiddenDaysInWorkWeek Tage die nicht in der Arbeitswoche angezeigt werden.
 */
export function getFormatedDateLabel(date: moment.Moment, viewMode: CalendarViewMode, hiddenDaysInWorkWeek: number[] = []): string
{
    var visibleDays = getVisibleDays(hiddenDaysInWorkWeek);

    switch (viewMode)
    {
        case CalendarViewMode.day:
        case CalendarViewMode.planning:
            return date.format("DD. MMMM YYYY");
        case CalendarViewMode.workWeek:
            {
                let firstDay = visibleDays.first();
                let lastDay = visibleDays[visibleDays.length - 1];
                let startOfWeek: moment.Moment = moment(date).getStartOfWeek(firstDay);
                let endOfWeek: moment.Moment = moment(date).getEndOfWeek(lastDay);

                if (startOfWeek.month() == endOfWeek.month())
                    return startOfWeek.format("DD.") + " - " + endOfWeek.format("DD. MMMM YYYY");
                else
                    return startOfWeek.format("DD. MMMM") + " - " + endOfWeek.format("DD. MMMM YYYY");
            }
        case CalendarViewMode.week:
            {
                let startOfWeek: moment.Moment = moment(date).startOf("isoWeek");
                let endOfWeek: moment.Moment = moment(date).endOf("isoWeek");

                if (startOfWeek.month() == endOfWeek.month())
                    return startOfWeek.format("DD.") + " - " + endOfWeek.format("DD. MMMM YYYY");
                else
                    return startOfWeek.format("DD. MMMM") + " - " + endOfWeek.format("DD. MMMM YYYY");
            }
        case CalendarViewMode.month:
            return date.format("MMMM YYYY");
    }
}

/**
 * Gibt die Nummern der sichtbaren Tage zurück.
 * @param hiddenDays Die Tage die nicht angezeigt werden sollen.
 */
export function getVisibleDays(hiddenDays: number[] = []): number[]
{
    var visibleDays: number[] = [1, 2, 3, 4, 5, 6, 7];
    return visibleDays.where(d => !hiddenDays.contains(d));
}

/**
 * Generiert die Wochen und Tage für die Datepicker und Calendar Komponenten.
 * @param date Das Datum anhand dessen die Wochen und Tage generiert werden sollen.
 * @param calculateForDatepicker Angabe, ob für den Datepicker generiert wird.
 */
export function getWeeks(date: moment.Moment, calculateForDatepicker: boolean = false): modelsCalendar.IWeek[]
{
    var selectedDate: moment.Moment = moment(date);
    var nowWithoutTime: moment.Moment = moment().startOf("day");
    var daysInMonth: number = date.daysInMonth();
    var firstDayInMonth: number = moment(date).startOf("month").day();
    firstDayInMonth = (firstDayInMonth === 0) ? 6 : firstDayInMonth - 1;
    var previousMonth: number = moment(date).subtract(1, "months").month();
    var nextMonth: number = moment(date).add(1, "months").month();
    var yearOfPreviousMonth: number = moment(date).subtract(1, "months").year();
    var yearOfNextMonth: number = moment(date).add(1, "months").year();
    var daysInPreviousMonth: number = moment([yearOfPreviousMonth, previousMonth, 1]).daysInMonth();

    var daysOfWeek: modelsCalendar.IDayOfWeek[] = [];
    var weeks: modelsCalendar.IWeek[] = [];
    var days: number = 42;

    if (!calculateForDatepicker)
    {
        days = daysInMonth + firstDayInMonth;
        let afterLastDays: number = days;

        while (afterLastDays > 7)
        {
            afterLastDays -= 7;
        }
        days += 7 - afterLastDays;
    }

    for (var i = 0, r = 0; i < days; i++)
    {
        var index = 1 + (i - firstDayInMonth);
        var day: moment.Moment;

        if (index <= 0)
        {
            day = moment([selectedDate.year(), selectedDate.month(), 1]).subtract(Math.abs(index - 1), "days");
        }
        else if (index > daysInMonth)
        {
            day = moment([selectedDate.year(), selectedDate.month(), daysInMonth]).add(index - daysInMonth, "days");
        }
        else
        {
            day = moment([selectedDate.year(), selectedDate.month(), index]);
        }

        var isToday: boolean = day.isSame(nowWithoutTime);
        var isOutsideCurrentMonth: boolean = i < firstDayInMonth || i >= (daysInMonth + firstDayInMonth);
        var isFirstDayOfMonth: boolean = day.isSame(moment([day.year(), day.month(), 1]));
        var dayNumber: number = 1 + (i - firstDayInMonth);
        var monthNumber: number = selectedDate.month();
        var yearNumber: number = selectedDate.year();

        if (isOutsideCurrentMonth)
        {
            if (i < firstDayInMonth)
            {
                dayNumber = daysInPreviousMonth + dayNumber;
                monthNumber = previousMonth;
                yearNumber = yearOfPreviousMonth;
            }
            else
            {
                dayNumber = dayNumber - daysInMonth;
                monthNumber = nextMonth;
                yearNumber = yearOfNextMonth;
            }
        }

        var currentDate: moment.Moment = moment([yearNumber, monthNumber, dayNumber]);
        var gridColumn: number = r + (calculateForDatepicker ? 2 : 1);
        var gridRow: number = weeks.length + 2;

        daysOfWeek.push({
            date: currentDate,
            isToday: isToday,
            isOutsideCurrentMonth: isOutsideCurrentMonth,
            isFirstDayOfMonth: isFirstDayOfMonth,
            gridColumn: gridColumn,
            gridRow: gridRow
        });

        if (++r == 7)
        {
            weeks.push({
                daysOfWeek: daysOfWeek
            });

            r = 0;
            daysOfWeek = [];
        }
    }

    return weeks;
}

export function getDays(date: moment.Moment, viewMode: CalendarViewMode, hiddenDaysInWorkWeek: number[] = []): modelsCalendar.IDayOfWeek[]
{
    var selectedDate: moment.Moment = moment(date);
    var startOfWeek: moment.Moment = moment(date).startOf("isoWeek");
    var nowStartOfWeek: moment.Moment = moment().startOf("isoWeek");
    var weekDay: moment.Moment = moment(date).startOf("isoWeek");
    var visibleDays = getVisibleDays(hiddenDaysInWorkWeek);

    var days: modelsCalendar.IDayOfWeek[] = [];

    if (viewMode === CalendarViewMode.day || viewMode === CalendarViewMode.planning)
    {
        days.push({
            date: moment(selectedDate),
            header: selectedDate.format("dd, DD. MMM."),
            isToday: selectedDate.date() === nowStartOfWeek.date(),
            isOutsideCurrentMonth: selectedDate.isoMonth() !== nowStartOfWeek.isoMonth()
        });
    }
    else
    {
        for (var i = 0; i < 7; i++)
        {
            weekDay = (i == 0) ? startOfWeek : weekDay.add(1, "days");

            if (viewMode === CalendarViewMode.workWeek && !visibleDays.any(d => d === weekDay.isoWeekday()))
                continue;

            days.push({
                date: moment(weekDay),
                header: weekDay.format("dd, DD. MMM."),
                isToday: weekDay.date() === nowStartOfWeek.date(),
                isOutsideCurrentMonth: weekDay.isoMonth() !== nowStartOfWeek.isoMonth()
            });
        }
    }

    return days;
}

export function getHours(): modelsCalendar.IHour[]
{
    var hours: modelsCalendar.IHour[] = [];

    for (var i = 0; i <= 23; i++)
    {
        hours.push({
            hour: new Time(i, 0, 0),
            range: getTimeRangeInterval()
        });
    }

    return hours;
}

export function getTimeRangeInterval(): number[]
{
    var timeScale = 15;
    var storage = localStorage.getJSON<modelsStorage.ICalendarStorage>(STORAGE_KEYS.calendar, true);
    if (storage != null && storage.timeScale > 0)
    {
        timeScale = storage.timeScale;
    }

    switch (timeScale)
    {
        case 30:
            return [30];
        case 15:
            return [15, 30, 45];
        case 10:
            return [10, 20, 30, 40, 50];
        case 5:
            return [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
        default:
            return [30];
    }
}

/**
 * Prüft ob ein Termin oder eine Sperre in den angegeben Zeitraum gehören.
 * @param date Das Datum welches zum prüfen verwendet werden soll.
 * @param appointment Der Termin der geprüft werden soll.
 * @param selectedObject Die momentan ausgewählte Kalendergruppe oder der ausgewählte Kalender.
 */
export function isAppointmentValid(date: moment.Moment, appointment: modelsGet.IAppointment, selectedObject: modelsCalendar.ISelectedObject, checkDate: boolean = true)
{
    const dateStartOfDay: moment.Moment = moment(date).startOf("day");
    const starttimeStartOfDay: moment.Moment = moment(appointment.starttime).startOf("day");
    const repeatUntilStartOfDay: moment.Moment = appointment.repeat_until ? moment(appointment.repeat_until).startOf("day") : null;

    if (appointment.appointment_type !== "block")
    {
        if (selectedObject.type === CalendarObjectType.all)
        {
            if (checkDate)
                return dateStartOfDay.isSame(starttimeStartOfDay);
            else
                return true;
        }
        else if (selectedObject.type === CalendarObjectType.calendarGroup)
        {
            if (checkDate)
                return (dateStartOfDay.isSame(starttimeStartOfDay) && appointment.calendar_group_id == selectedObject.data.id);
            else
                return appointment.calendar_group_id == selectedObject.data.id;
        }
        else
        {
            if (checkDate)
                return (dateStartOfDay.isSame(starttimeStartOfDay) && appointment.calendar_id == selectedObject.data.id);
            else
                return appointment.calendar_id == selectedObject.data.id;
        }
    }
    else if (appointment.appointment_type === "block")
    {
        if (selectedObject.type === CalendarObjectType.all)
        {
            if (checkDate)
                return dateStartOfDay.isSame(starttimeStartOfDay);
            else
                return true;
        }
        else if (selectedObject.type === CalendarObjectType.calendarGroup)
        {
            if (checkDate)
                return ((dateStartOfDay.isSame(starttimeStartOfDay) || repeatUntilStartOfDay && (dateStartOfDay >= starttimeStartOfDay && dateStartOfDay <= repeatUntilStartOfDay)) && appointment.calendar_group_id == selectedObject.data.id);
            else
                return appointment.calendar_group_id == selectedObject.data.id;
        }
        else
        {
            if (checkDate)
                return ((dateStartOfDay.isSame(starttimeStartOfDay) || repeatUntilStartOfDay && (dateStartOfDay >= starttimeStartOfDay && dateStartOfDay <= repeatUntilStartOfDay)) && appointment.calendar_id == selectedObject.data.id);
            else
                return appointment.calendar_id == selectedObject.data.id;
        }
    }
}

/**
 * Generiert den Info Text wenn Labels geändert wurden.
 * @param activity Ein Objekt mit Aktivitäten.
 * @param details
 */
export function getLabelActivityText(activity: modelsGet.IActivity, details: boolean = false): string
{
    var value: string;

    if ((activity.removed_labels != null && activity.removed_labels.length > 0) && (activity.added_labels != null && activity.added_labels.length > 0))
    {
        if (activity.added_labels.length > 1)
            value = `${activity.editor_fullname} hat ${activity.added_labels.length} Labels hinzugefügt und ${activity.removed_labels.length} entfernt.`;
        else
            value = `${activity.editor_fullname} hat ${activity.added_labels.length} Label hinzugefügt und ${activity.removed_labels.length} entfernt.`;
    }
    else if (activity.added_labels != null && activity.added_labels.length > 0)
    {
        if (activity.added_labels.length > 1)
            value = `${activity.editor_fullname} hat ${activity.added_labels.length} Labels hinzugefügt.`;
        else
            value = `${activity.editor_fullname} hat ${activity.added_labels.length} Label hinzugefügt.`;
    }
    else if (activity.removed_labels != null && activity.removed_labels.length > 0)
    {
        if (activity.removed_labels.length > 1)
            value = `${activity.editor_fullname} hat ${activity.removed_labels.length} Labels entfernt.`;
        else
            value = `${activity.editor_fullname} hat ${activity.removed_labels.length} Label entfernt.`;
    }

    if (details)
        return value.replace(activity.editor_fullname + " h", "H");
    else
        return value;
}

export function isTimeInRange(hour: string, viewMode: CalendarViewMode, range?: number, selectedObject?: modelsCalendar.ISelectedObject, selectedCalendar?: modelsGet.ICalendar, date?: moment.Moment): boolean
{    
    var settings: modelsGet.ICalendarSettings;
    if (selectedObject != null && selectedObject.type === CalendarObjectType.calendar)
    {
        settings = JSON.parse(selectedObject.data.settings);
    }
    else
    {
        settings = JSON.parse(selectedCalendar.settings);
    }

    var availableRangeStart: Time = Time.parse(settings.time.range[0]);
    var availableRangeEnd: Time = Time.parse(settings.time.range[1]);
    var time: Time = Time.parse(hour);
    time = new Time(time.Hours, time.Minutes + ((range > 0) ? range : 0), time.Seconds);

    if (time.lessThan(availableRangeStart) || (time.greaterThan(availableRangeEnd) || time.equals(availableRangeEnd)))
    {
        return false;
    }

    if (settings.time.locks != null && settings.time.locks.length > 0)
    {
        var engData: any = moment.localeData('en');
        for (let lock of settings.time.locks.where(l => l.weekday === engData.weekdays()[date.day()].toLowerCase()))
        {
            var from: Time = Time.parse(lock.from);
            var to: Time = Time.parse(lock.to);

            if ((time.equals(from) || time.greaterThan(from)) && time.lessThan(to))
            {
                return false;
            }
        }
    }

    return true;
}

export function isTimeRowSelected(day: any, clickedTime: string, clickedRange: number, clickedCalendar: modelsGet.ICalendar): boolean
{
    var time: Time = Time.parse(clickedTime);
    time = new Time(time.Hours, time.Minutes + ((clickedRange > 0) ? clickedRange : 0), time.Seconds);

    if (clickedCalendar != null)
        return (this.selectedDate().isSame(day.date, 'day') && this.selectedTime() === time.toString() && clickedCalendar.id === this.selectedCalendarInPlanningView().id);
    else
        return (this.selectedDate().isSame(day.date, 'day') && this.selectedTime() === time.toString());
}

export function showEvent(appointment: modelsGet.IAppointment, user: modelsGet.IUser, viewMode: CalendarViewMode, selectedObject: modelsCalendar.ISelectedObject, selectedCalendar?: modelsGet.ICalendar): number
{
    var settings: modelsGet.ICalendarSettings;
    if (selectedObject != null && selectedObject.type === CalendarObjectType.calendar)
    {
        settings = JSON.parse(selectedObject.data.settings);
    }
    else
    {
        settings = JSON.parse(selectedCalendar.settings);
    }

    if (appointment.appointment_type !== "not_authorized")
        return 1; // Darf Termin sehen.

    if (settings.time.show_free_blocks && user.claims.can_create_event_freely)
    {
        return 2; // Darf Termin sehen aber als "Belegt".
    }
    else if (settings.time.show_free_blocks && !user.claims.can_create_event_freely)
    {
        if (selectedObject.type === CalendarObjectType.calendarGroup && viewMode !== CalendarViewMode.planning)
            return 2
        else
            return 3; // Termin wird nicht angezeigt. Auch nicht als "Belegt".
    }
    else
    {
        return 2;
    }
}

export function showBlocks(selectedObject: modelsCalendar.ISelectedObject, viewMode: CalendarViewMode, calendarId?: number): boolean
{
    var settings: modelsGet.ICalendarSettings;

    switch (viewMode)
    {
        case CalendarViewMode.planning:
            {
                if (selectedObject.calendarsInGroup != null && selectedObject.calendarsInGroup.length > 0)
                {
                    settings = JSON.parse(selectedObject.calendarsInGroup.where(c => c.id === calendarId).first().settings);
                    return settings.time.show_free_blocks;
                }
                else
                {
                    settings = JSON.parse(selectedObject.data.settings);
                    return settings.time.show_free_blocks;
                }
            }
        default:
            {
                if (selectedObject.calendarsInGroup != null && selectedObject.calendarsInGroup.length > 0)
                {
                    return false;
                }
                else
                {
                    settings = JSON.parse(selectedObject.data.settings);
                    return settings.time.show_free_blocks;
                }
            }
    }
}

export function showLockedBlock(block: modelsCalendar.IAvailableAppointment, user: modelsGet.IUser): boolean
{
    if (user.claims.can_create_event_freely)
        return false;

    if (!user.claims.can_see_all_events)
        return !(block.userId != null && block.userId === user.id);

    return false;
}

/**
 * Positioniert die Termine anhand ihres Zeitraumes an der Zeiskala.
 */
export function getEventPosition(appointment: modelsGet.IAppointment): { setuptimeBefore: React.CSSProperties, setuptimeAfter: React.CSSProperties, content: React.CSSProperties, base: React.CSSProperties }
{
    var starttime: moment.Moment = moment(appointment.starttime);
    var endtime: moment.Moment = moment(appointment.endtime);
    var setuptimeBefore: number = appointment.setuptime_before;
    var setuptimeAfter: number = appointment.setuptime_after;

    // Die momentan eingestellte Zeitskala ermitteln.
    var currentlySetTimeScale: number = 15;
    var storage = localStorage.getJSON<modelsStorage.ICalendarStorage>(STORAGE_KEYS.calendar, true);
    if (storage != null && storage.timeScale > 0)
        currentlySetTimeScale = storage.timeScale;

    // Höhe der Zeitskale berechnen.
    var timeScaleHeight: number = 34;
    switch (currentlySetTimeScale)
    {
        case 30:
            timeScaleHeight = timeScaleHeight * 2;
            break;
        case 15:
            timeScaleHeight = timeScaleHeight * 4;
            break;
        case 10:
            timeScaleHeight = timeScaleHeight * 6;
            break;
        case 5:
            timeScaleHeight = timeScaleHeight * 12;
            break;
        default:
            timeScaleHeight = timeScaleHeight * 2;
            break;
    }

    // Position des Events ermitteln.
    var timeScaleTopPosition: number = 0;
    var starttimeHour: number = starttime.hour();
    var starttimeMinutes: number = starttime.minutes();
    if (starttimeHour === 0)
        timeScaleTopPosition = 0;
    else
        timeScaleTopPosition = timeScaleHeight * starttimeHour + getPixelOutOfMinutes(starttimeMinutes, timeScaleHeight);

    // Die höhe des Events ermitteln.
    var eventMinutes = moment(endtime).diff(moment(starttime), "minutes");
    var heightPixel = getPixelOutOfMinutes(eventMinutes, timeScaleHeight);

    // Ermittelte Daten dem Event übergeben.
    // Height - 1 damit Unterschiedliche Termine nicht direkt aneinander abschließen.
    var setuptimeBeforeHeight: number = (setuptimeBefore > 0 ? getPixelOutOfMinutes(setuptimeBefore, timeScaleHeight) : null);
    var setuptimeAfterHeight: number = (setuptimeAfter > 0 ? getPixelOutOfMinutes(setuptimeAfter, timeScaleHeight) : null);
    if (heightPixel < 34)
    {
        return {
            setuptimeBefore: {
                height: setuptimeBeforeHeight
            },
            setuptimeAfter: {
                height: setuptimeAfterHeight
            },
            content: {
                height: heightPixel - 1,
                padding: "1px 5px"
            },
            base: {
                top: (setuptimeBeforeHeight != null ? timeScaleTopPosition - setuptimeBeforeHeight : timeScaleTopPosition)
            }
        }
    }
    else if (heightPixel < 17)
    {
        return {
            setuptimeBefore: {
                height: setuptimeBeforeHeight
            },
            setuptimeAfter: {
                height: setuptimeAfterHeight
            },
            content: {
                height: 17,
                padding: "1px 5px"
            },
            base: {
                top: (setuptimeBeforeHeight != null ? timeScaleTopPosition - setuptimeBeforeHeight : timeScaleTopPosition)
            }
        }
    }
    else
    {
        return {
            setuptimeBefore: {
                height: setuptimeBeforeHeight
            },
            setuptimeAfter: {
                height: setuptimeAfterHeight
            },
            content: {
                height: heightPixel - 1
            },
            base: {
                top: (setuptimeBeforeHeight != null ? timeScaleTopPosition - setuptimeBeforeHeight : timeScaleTopPosition)
            }
        }
    }
}

export function getLockPosition(appointment: any): React.CSSProperties
{
    if (appointment == null)
        return {};

    // Start- und Enddatum ermitteln.
    var starttime: moment.Moment = moment(appointment.starttime).subtract(appointment.setuptime_before || 0, "minutes");
    var endtime: moment.Moment = moment(appointment.endtime).add(appointment.setuptime_after || 0, "minutes");

    // Die momentan eingestellte Zeitskala ermitteln.
    var currentlySetTimeScale: number = 15;
    var storage = localStorage.getJSON<modelsStorage.ICalendarStorage>(STORAGE_KEYS.calendar, true);
    if (storage != null && storage.timeScale > 0)
        currentlySetTimeScale = storage.timeScale;

    // Höhe der Zeitskale berechnen.
    var timeScaleHeight: number = 34;
    switch (currentlySetTimeScale)
    {
        case 30:
            timeScaleHeight = timeScaleHeight * 2;
            break;
        case 15:
            timeScaleHeight = timeScaleHeight * 4;
            break;
        case 10:
            timeScaleHeight = timeScaleHeight * 6;
            break;
        case 5:
            timeScaleHeight = timeScaleHeight * 12;
            break;
        default:
            timeScaleHeight = timeScaleHeight * 2;
            break;
    }

    // Position des Events ermitteln.
    var timeScaleTopPosition: number = 0;
    var starttimeHour: number = starttime.hour();
    var starttimeMinutes: number = starttime.minutes();
    if (starttimeHour === 0)
        timeScaleTopPosition = 0;
    else
        timeScaleTopPosition = timeScaleHeight * starttimeHour + getPixelOutOfMinutes(starttimeMinutes, timeScaleHeight);

    // Die höhe des Events ermitteln.
    var eventMinutes = moment(endtime).diff(moment(starttime), "minutes");
    var heightPixel = getPixelOutOfMinutes(eventMinutes, timeScaleHeight);

    // Ermittelte Daten dem Event übergeben.
    // Height - 1 damit Unterschiedliche Termine nicht direkt aneinander abschließen.
    if (heightPixel < 34)
    {
        return {
            top: timeScaleTopPosition,
            height: heightPixel - 1,
            padding: "1px 5px"
        };
    }
    else if (heightPixel < 17)
    {
        return {
            top: timeScaleTopPosition,
            height: 17,
            padding: "1px 5px"
        };
    }
    else
    {
        return {
            top: timeScaleTopPosition,
            height: heightPixel - 1
        };
    }
}

/**
 * Termine die in ihrem Zeitraum überlappen, nebeneinander ausrichten.
 */
export function arrangeEvents(): void
{
    var column_index = 0;

    $(".calendar-dv-cols_col, .calendar-pv-cols_col").each((index, elem) =>
    {
        var column: JQuery = $(elem);
        var block_width = column.width() - 9;

        var columns = [];
        var lastEventEnding = null;

        // Create an array of all events
        var events = column.find(".event, .lock, [data-popup-provider='event']").map((index, o) =>
        {
            var evt = $(o);

            if (evt.parent("[data-popup-provider]").length <= 0)
            {
                var top = evt.offset().top;
                return {
                    'obj': evt,
                    'top': top,
                    'bottom': top + evt.height()
                };
            }
        }).get();

        // Sort it by starting time, and then by ending time.
        events = events.sort((e1: any, e2: any) =>
        {
            if (e1.top < e2.top) return -1;
            if (e1.top > e2.top) return 1;
            if (e1.bottom < e2.bottom) return -1;
            if (e1.bottom > e2.bottom) return 1;
            return 0;
        });

        // Iterate over the sorted array
        $(events).each((index, e) =>
        {

            // Check if a new event group needs to be started
            if (lastEventEnding !== null && (e as any).top >= lastEventEnding)
            {
                // The latest event is later than any of the event in the 
                // current group. There is no overlap. Output the current 
                // event group and start a new event group.
                packEvents(columns, block_width);
                columns = [];  // This starts new event group.
                lastEventEnding = null;
            }

            // Try to place the event inside the existing columns
            var placed = false;
            for (var i = 0; i < columns.length; i++)
            {
                var col = columns[i];
                if (!collidesWith(col[col.length - 1], e))
                {
                    col.push(e);
                    placed = true;
                    break;
                }
            }

            // It was not possible to place the event. Add a new column 
            // for the current event group.
            if (!placed)
            {
                columns.push([e]);
            }

            // Remember the latest event end time of the current group. 
            // This is later used to determine if a new groups starts.
            if (lastEventEnding === null || (e as any).bottom > lastEventEnding)
            {
                lastEventEnding = (e as any).bottom;
            }
        });

        if (columns.length > 0)
        {
            packEvents(columns, block_width);
        }
    });
}

/**
 * Function does the layout for a group of events.
 * @param columns
 * @param block_width
 */
function packEvents(columns, block_width)
{
    var n = columns.length;
    for (var i = 0; i < n; i++)
    {
        var col = columns[i];
        for (var j = 0; j < col.length; j++)
        {
            var bubble = col[j];
            var colSpan = expandEvent(bubble, i, columns);

            if (i === 0)
            {
                bubble.obj.css('left', (i / n) * 100 + '%');
            }
            else
            {
                bubble.obj.css('left', "calc(" + (i / n) * 100 + "% - " + (3 * i) + "px)");
            }

            bubble.obj.css('width', block_width * colSpan / n - 1);
        }
    }
}

/**
 * Check if two events collide.
 * @param a
 * @param b
 */
function collidesWith(a, b)
{
    return a.bottom > b.top && a.top < b.bottom;
}

/**
 * Expand events at the far right to use up any remaining space.
 * Checks how many columns the event can expand into, without
 * colliding with other events. Step 5 in the algorithm.
 * @param ev
 * @param iColumn
 * @param columns
 */
function expandEvent(ev, iColumn, columns)
{
    var colSpan = 1;

    // To see the output without event expansion, uncomment 
    // the line below. Watch column 3 in the output.
    //return colSpan;

    for (var i = iColumn + 1; i < columns.length; i++) 
    {
        var col = columns[i];
        for (var j = 0; j < col.length; j++)
        {
            var ev1 = col[j];
            if (collidesWith(ev, ev1))
            {
                return colSpan;
            }
        }
        colSpan++;
    }
    return colSpan;
}